/**
 * Gate Attendance Service - API client for the school-gate arrival/departure
 * log (routers/gate_attendance.py) — distinct from classroom roll-call
 * attendance (attendanceService) and the QR pickup-dispatch system
 * (securityService).
 */
import api from '../utils/api';

const gateAttendanceService = {
  checkIn: async (studentId, notes) => {
    try {
      return await api.post('/api/gate-attendance/check-in', { student_id: studentId, notes: notes || undefined });
    } catch (error) {
      console.error('Error recording gate check-in:', error);
      throw error;
    }
  },

  checkOut: async (studentId, notes) => {
    try {
      return await api.post('/api/gate-attendance/check-out', { student_id: studentId, notes: notes || undefined });
    } catch (error) {
      console.error('Error recording gate check-out:', error);
      throw error;
    }
  },

  checkInByScan: async (cardNumber, notes) => {
    try {
      return await api.post('/api/gate-attendance/check-in/scan', { card_number: cardNumber, notes: notes || undefined });
    } catch (error) {
      console.error('Error recording gate check-in by scan:', error);
      throw error;
    }
  },

  checkOutByScan: async (cardNumber, notes) => {
    try {
      return await api.post('/api/gate-attendance/check-out/scan', { card_number: cardNumber, notes: notes || undefined });
    } catch (error) {
      console.error('Error recording gate check-out by scan:', error);
      throw error;
    }
  },

  getToday: async (classId) => {
    try {
      return await api.get('/api/gate-attendance/today', { params: classId ? { class_id: classId } : {} });
    } catch (error) {
      console.error('Error fetching today\'s gate attendance:', error);
      throw error;
    }
  },

  getLateArrivals: async (params = {}) => {
    try {
      return await api.get('/api/gate-attendance/late-arrivals', { params });
    } catch (error) {
      console.error('Error fetching late arrivals:', error);
      throw error;
    }
  },

  getLatePickups: async (params = {}) => {
    try {
      return await api.get('/api/gate-attendance/late-pickups', { params });
    } catch (error) {
      console.error('Error fetching late pickups:', error);
      throw error;
    }
  },

  getDailySummary: async (date) => {
    try {
      return await api.get('/api/gate-attendance/daily-summary', { params: date ? { date } : {} });
    } catch (error) {
      console.error('Error fetching daily gate summary:', error);
      throw error;
    }
  },

  getSettings: async () => {
    try {
      return await api.get('/api/gate-attendance/settings');
    } catch (error) {
      console.error('Error fetching gate attendance settings:', error);
      throw error;
    }
  },

  updateSettings: async (data) => {
    try {
      return await api.put('/api/gate-attendance/settings', data);
    } catch (error) {
      console.error('Error updating gate attendance settings:', error);
      throw error;
    }
  },

  // Sets the PIN a student was assigned at a fingerprint/face terminal's
  // own local enrollment step. Deliberately calls the existing
  // canteen-wallet endpoint (routers/canteen_wallet.py) rather than a new
  // one — it's the same Student.biometric_device_pin field a gate-scan
  // device resolves against, not a separate one for this feature.
  setBiometricPin: async (studentId, pin) => {
    try {
      return await api.put(`/api/canteen-wallet/students/${studentId}/biometric-pin`, { pin });
    } catch (error) {
      console.error('Error setting student biometric PIN:', error);
      throw error;
    }
  },
};

export default gateAttendanceService;
